// Data hook for the public, read-only sport club home page. Like the athlete
// profile, a club home does not change second-to-second, so it loads once (no
// polling). Resolves to `notfound` when the token is unknown or the page is not
// public, so a stale or private link shows a friendly message rather than an
// error.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicClubHome } from '../../domain';
import { fetchPublicClubHome } from '../../data/publicClient';

export type PublicClubStatus = 'loading' | 'ready' | 'notfound' | 'error';

export interface PublicClubResult {
  home: PublicClubHome | null;
  status: PublicClubStatus;
  error: string | null;
  reload: () => void;
}

export function useClubHome(token: string): PublicClubResult {
  const [home, setHome] = useState<PublicClubHome | null>(null);
  const [status, setStatus] = useState<PublicClubStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const next = await fetchPublicClubHome(token);
      if (!mounted.current) return;
      if (next === null) {
        setHome(null);
        setStatus('notfound');
      } else {
        setHome(next);
        setStatus('ready');
        setError(null);
      }
    } catch (e: unknown) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load club.');
      setStatus('error');
    }
  }, [token]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  return { home, status, error, reload: () => void load() };
}
